<template>
  <div
    class="BookingPromoCode"
    :class="{ 'BookingPromoCode--expanded': expanded }">
    <div class="BookingPromoCode-header" @click="toggleExpand">
      <ai-typo
        as="h4"
        variant="heading-04"
        class="BookingPromoCode-header-title">
        {{ $t('booking.recap.promoCode.title') }}
      </ai-typo>
      <ai-icon
        :size="24"
        name="chevron"
        class="BookingPromoCode-header-icon"
        :class="{ 'BookingPromoCode-header-icon--expanded': expanded }" />
    </div>
    <div v-if="expanded" class="BookingPromoCode-content">
      <ai-input
        id="promoCode"
        v-model="promoCode"
        label=""
        :placeholder="$t('booking.recap.promoCode.placeholder')"
        type="text"
        class="custom-ai-input"
        @keyup.enter="applyPromoCode" />
      <ai-button
        :label="$t('booking.recap.promoCode.submit')"
        :loading="loading"
        :disabled="loading"
        @click="applyPromoCode" />
    </div>
    <div v-if="errorMessage" class="BookingPromoCode-errorMessage">
      <ai-tool-tip
        role="alert"
        variant="warning"
        align-item="center"
        :label="errorMessage"
        background="red-100"
        icon-fill="red-700" />
    </div>
    <div v-if="validMessage" class="BookingPromoCode-validMessage">
      <ai-tool-tip
        role="alert"
        variant="check-green"
        align-item="center"
        :label="validMessage"
        background="green-100" />
    </div>
    <div v-if="noOffersMessage" class="BookingPromoCode-noOffersMessage">
      <ai-tool-tip
        role="alert"
        variant="warning"
        align-item="center"
        :label="noOffersMessage"
        background="red-100"
        icon-fill="red-700" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useApolloClient } from '@vue/apollo-composable';

import { usePreferentialCode } from '~/composables/useIdentificationPlaintext';

import { useBookingStore } from '~~/domains/booking';
import { CheckCorporateCodesDocument } from '~~/domains/graphql';
import { useUserStore } from '~~/domains/user';

const expanded = ref(false);
const preferentialCode = usePreferentialCode();
const router = useRouter();
const route = useRoute();
const promoCode = ref(preferentialCode ?? '');
const errorMessage = ref('');
const validMessage = ref('');
const noOffersMessage = ref('');
const loading = ref(false);
const bookingStore = useBookingStore();
const userStore = useUserStore();
const { t } = useI18n();

const emit = defineEmits(['promoCodeApplied']);
const hasMessage = computed(() => {
  return errorMessage.value || validMessage.value || noOffersMessage.value;
});

const toggleExpand = () => {
  expanded.value = !expanded.value;
};

const applyPromoCode = () => {
  loading.value = true;
  errorMessage.value = '';
  validMessage.value = '';
  noOffersMessage.value = '';
  bookingStore.promotionalInformation = {
    ...bookingStore.promotionalInformation,
    preferentialCode: promoCode.value,
  };

  if (!promoCode.value) {
    const query = { ...router.currentRoute.value.query };
    delete query.preferentialCode;
    router.push({
      query,
      replace: true,
    });
  }

  const { client } = useApolloClient();
  client
    .query({
      query: CheckCorporateCodesDocument,
      variables: {
        cardNumber: userStore.loyaltyCardNumber,
        ...bookingStore.promotionalInformation,
      },
      fetchPolicy: 'no-cache',
    })
    .then(result => {
      if (result.loading) {
        return;
      }

      // This line should be removed when @ACCORDAI-2027 will be merge
      userStore.loadAccessToken();

      const query = { ...router.currentRoute.value.query };
      if (promoCode.value) {
        query.preferentialCode = promoCode.value;
      } else {
        delete query.preferentialCode;
      }

      router.push({ query, replace: true });
      loading.value = false;
      emit('promoCodeApplied');
    })
    .catch(error => {
      loading.value = false;
      validMessage.value = '';
      if (error.message) {
        errorMessage.value = "Promo code doesn't exist";
      }
      const query = { ...router.currentRoute.value.query };
      delete query.preferentialCode;
      router.push({
        query,
        replace: true,
      });
    });
};

watch(
  () => preferentialCode,
  () => {
    promoCode.value = preferentialCode ?? '';
  },
);

watch(
  () => bookingStore.roomResultTrigger,
  () => {
    errorMessage.value = '';
    const preferentialCode = usePreferentialCode();
    if (!preferentialCode) return;
    if (!bookingStore.hasOffersWithPromoCode) {
      validMessage.value = '';
      noOffersMessage.value = t('booking.recap.promoCode.noOffers', {
        promoCode: preferentialCode,
      });
    } else {
      validMessage.value = t('booking.recap.promoCode.valid', {
        promoCode: preferentialCode,
      });
      noOffersMessage.value = '';
    }
  },
);

watch(hasMessage, newValue => {
  if (newValue) {
    expanded.value = true;
  }
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/functions' as func;

.BookingPromoCode {
  background-color: colors.$gold-100;
  padding: constants.$padding-01;
  margin-bottom: constants.$inner-00;
}
.BookingPromoCode-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}
.BookingPromoCode-header-title {
  color: colors.$gold-900;
}
.BookingPromoCode-header-icon {
  transition: transform 200ms;
}
.BookingPromoCode-header-icon--expanded {
  transform: rotate(180deg);
}
.BookingPromoCode-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: constants.$margin-01;
}
.BookingPromoCode-content .custom-ai-input {
  flex: 1;
  margin-right: constants.$margin-01;
}
.BookingPromoCode-errorMessage {
  margin-top: constants.$margin-01;
}
.BookingPromoCode-validMessage {
  margin-top: constants.$margin-01;
}

.BookingPromoCode-noOffersMessage {
  margin-top: constants.$margin-01;
}
</style>
